import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, X, Sparkles, ChevronDown, ChevronUp, Calendar as CalendarIcon, Eye } from "lucide-react";
import { format as fmtDate } from "date-fns";
import DatePickerSheet from "@/components/DatePickerSheet";
import QuestionBuilder from "@/components/admin/QuestionBuilder";
import QuestionPreview from "@/components/workspace/QuestionPreview";
import { validateQuestions } from "@/lib/questionValidation";
import { formatApiError } from "@/lib/errors";
import {
  adminCreateTask, adminDeleteTask, adminForceSelect, adminGetInstructionTemplate,
  adminListTasks, adminListUsers, adminPreviewGrade, getPublicCountries,
} from "@/lib/afritask";

const TYPES = [
  "text_classification", "image_labeling", "translation",
  "audio_transcription", "prompt_rating", "voice_rating",
];
const LANGS = ["English", "Yoruba", "Igbo", "Hausa", "Pidgin"];

// Default payload shape per task type. Rendered as structured fields in the form.
const emptyPayloadFor = (t) => {
  switch (t) {
    case "text_classification":
      return { text: "", labels: ["positive", "neutral", "negative"], correct_label: "positive" };
    case "image_labeling":
      return { image_url: "", ground_truth_tags: [], min_tags: 3, max_tags: 10 };
    case "translation":
      return { source_text: "", source_language: "English", target_language: "Yoruba",
               reference_translation: "", min_chars: 20 };
    case "audio_transcription":
      return { audio_url: "", language: "English", reference_transcript: "" };
    case "prompt_rating":
      return { prompt: "", response: "", criteria: ["Accuracy", "Clarity", "Helpfulness"],
               expected_ratings: { Accuracy: 5, Clarity: 5, Helpfulness: 5 }, expected_overall: 5 };
    case "voice_rating":
      return { prompt: "Voice clip to rate", response: "",
               criteria: ["Accuracy", "Fluency", "Cultural fit"],
               expected_ratings: { Accuracy: 5, Fluency: 5, "Cultural fit": 5 }, expected_overall: 5 };
    default:
      return {};
  }
};

export default function AdminTasks() {
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => baseForm("text_classification"));
  const [contextOpen, setContextOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSample, setPreviewSample] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  // Country targeting sources — countries list + facets (for reach estimate)
  const [countries, setCountries] = useState([]);          // [{iso2, name}]
  const [countryFacets, setCountryFacets] = useState([]);  // [{country, count}]
  const [showAllCountries, setShowAllCountries] = useState(false);

  useEffect(() => {
    getPublicCountries().then((d) => setCountries(d.countries || [])).catch(() => {});
    adminListUsers({}).then((d) => setCountryFacets(d.country_facets || [])).catch(() => {});
  }, []);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [questionsPreviewOpen, setQuestionsPreviewOpen] = useState(false);
  // Pre-baked instruction template state (Phase C2).
  // • templateCache: {task_type: template_string}. Populated on demand from the backend.
  // • knownTemplatesRef: set of all template strings we've ever seen, so we can
  //   safely swap when the user hasn't customized (current text matches a known template).
  const [templateCache, setTemplateCache] = useState({});
  const knownTemplatesRef = useRef(new Set());
  const [resetTemplateOpen, setResetTemplateOpen] = useState(false);

  const rememberTemplate = useCallback((tpl) => {
    if (tpl) knownTemplatesRef.current.add(tpl);
  }, []);

  const fetchTemplate = useCallback(async (task_type) => {
    if (templateCache[task_type] !== undefined) return templateCache[task_type];
    try {
      const r = await adminGetInstructionTemplate(task_type);
      const tpl = r?.template || "";
      setTemplateCache((c) => ({ ...c, [task_type]: tpl }));
      rememberTemplate(tpl);
      return tpl;
    } catch { return ""; }
  }, [templateCache, rememberTemplate]);

  // Auto-populate Instructions when the create dialog opens for a fresh task.
  useEffect(() => {
    if (!open) return;
    if ((form.instructions || "").trim()) return;
    fetchTemplate(form.task_type).then((tpl) => {
      if (!tpl) return;
      // Populate only if the admin hasn't typed anything since we started.
      setForm((f) => (!(f.instructions || "").trim() ? { ...f, instructions: tpl } : f));
    });
  }, [open, form.task_type, form.instructions, fetchTemplate]);

  const load = useCallback(async () => { setTasks(await adminListTasks()); }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = (task_type = form.task_type) => setForm(baseForm(task_type));

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    // Preflight numeric ranges (mirrors backend pydantic constraints so admins
    // get a clear inline message instead of a raw 422 pydantic error array).
    const numErr = validateNumericFields(form);
    if (numErr) { toast.error(numErr); return; }
    const hasQuestions = (form.questions || []).length > 0;
    // Validate task questions FIRST (Phase B primary authoring path).
    const qv = validateQuestions(form.questions || []);
    if (!qv.ok) { toast.error(qv.errors[0]); return; }
    // Only enforce legacy per-task-type payload validation when the task is
    // authored the legacy way (no task questions). Phase B tasks use
    // `questions[]` as ground truth so the legacy payload is optional.
    if (!hasQuestions) {
      const err = validatePayload(form.task_type, form.payload);
      if (err) { toast.error(err); return; }
    }
    // Audience validation
    if (form.target_scope === "specific" && (form.target_countries || []).length === 0) {
      toast.error("Pick at least one country");
      return;
    }
    try {
      const entryDeadline = form.deadline || new Date(Date.now() + 24 * 3600_000).toISOString();
      await adminCreateTask({
        title: form.title,
        description: form.description,
        task_type: form.task_type,
        language: form.language,
        entry_cost_points: Number(form.entry_cost_points),
        winner_reward_points: Number(form.winner_reward_points),
        winners_count: Number(form.winners_count),
        entry_cap: Number(form.entry_cap),
        require_perfect_score: !!form.require_perfect_score,
        min_winning_score: form.require_perfect_score ? 100 : Number(form.min_winning_score),
        deadline: entryDeadline,
        submission_deadline: form.submission_deadline || new Date(new Date(entryDeadline).getTime() + 24 * 3600_000).toISOString(),
        task_payload: form.payload,
        task_context: form.task_context || "",
        grading_notes: form.grading_notes || "",
        example_correct: form.example_correct || "",
        example_incorrect: form.example_incorrect || "",
        instructions: form.instructions || "",
        questions: form.questions || [],
        target_scope: form.target_scope || "all",
        target_countries: form.target_scope === "specific" ? (form.target_countries || []) : [],
      });
      if (qv.warnings.length) {
        toast.success(`Task created · ${qv.warnings.length} question(s) not graded`);
      } else {
        toast.success("Task created");
      }
      setOpen(false);
      resetForm("text_classification");
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
    }
  };

  const forceSelect = async (id) => {
    try {
      await adminForceSelect(id);
      toast.success("Selection ran");
      load();
    } catch (e) { toast.error(formatApiError(e, "Force-select failed")); }
  };

  const del = async (id) => {
    // Dry-run first
    let dry;
    try { dry = await adminDeleteTask(id, false); }
    catch (e) { toast.error(formatApiError(e, "Dry-run failed")); return; }
    const w = dry.would_delete || {};
    const msg = `This will:
• Delete ${w.task_entries || 0} task entries
• Refund ${w.total_refund_points || 0} pts across ${w.refunds_to_issue || 0} entrants
• Permanently delete the task

Continue?`;
    if (!confirm(msg)) return;
    try {
      const r = await adminDeleteTask(id, true);
      toast.success(`Deleted · refunded ${r.deleted.total_refund_points} pts to ${r.deleted.refunds_issued} entrants`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Delete failed")); }
  };

  const setPayload = (patch) => setForm((f) => ({ ...f, payload: { ...f.payload, ...patch } }));

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={() => { resetForm("text_classification"); setOpen(true); }}
                className="bg-amber-700 hover:bg-amber-800" data-testid="admin-create-task-btn">
          <Plus className="mr-1 h-4 w-4" /> Create task
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Lang</TableHead>
                <TableHead>Entry</TableHead>
                <TableHead>Reward</TableHead>
                <TableHead>Cap</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((t) => (
                <TableRow key={t.task_id}>
                  <TableCell className="font-medium">{t.title}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{t.task_type}</Badge></TableCell>
                  <TableCell>{t.language}</TableCell>
                  <TableCell className="text-xs tabular-nums">{t.entry_cost_points}</TableCell>
                  <TableCell className="text-xs tabular-nums">{t.reward_points}</TableCell>
                  <TableCell className="text-xs tabular-nums">{t.entered_count}/{t.applicant_cap}</TableCell>
                  <TableCell data-testid={`task-target-${t.task_id}`}>
                    {t.target_scope === "specific" && (t.target_countries || []).length > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-[#FDF3E4] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#B45309] ring-1 ring-[#B45309]/15">
                        {t.target_countries[0]}
                        {t.target_countries.length > 1 && (
                          <span className="text-[9px] font-normal text-[#B45309]/80">
                            +{t.target_countries.length - 1} more
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="rounded-md bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500">
                        Global
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={t.status === "open" ? "default" : "secondary"}
                           className={t.status === "open" ? "bg-emerald-600 hover:bg-emerald-600" : ""}>
                      {t.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="flex gap-1">
                    {t.status === "open" && t.entered_count >= 3 && (
                      <Button size="sm" variant="outline" onClick={() => forceSelect(t.task_id)}
                              data-testid={`force-${t.task_id}`}>
                        Force select
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(t.task_id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create new task</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Configure the task, rewards and grading rules workers will see.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input placeholder="e.g. Yoruba news sentiment"
                     value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     data-testid="admin-task-title" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Instructions (shown to workers before they start)</Label>
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => setResetTemplateOpen(true)}
                    className="text-[10px] font-medium text-amber-700 hover:underline"
                    data-testid="admin-instructions-reset">
                    Reset to default
                  </button>
                  <span className="text-[10px] text-neutral-400">{(form.instructions || "").length}/4000</span>
                </div>
              </div>
              <Textarea rows={3} maxLength={4000}
                value={form.instructions || ""}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                placeholder="What should workers know before answering? E.g. 'This task studies how young Nigerians describe their favourite jollof recipe. Answer honestly — no wrong answers.'"
                data-testid="admin-task-instructions" />
            </div>
            <div className="rounded-lg border border-neutral-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-neutral-900">Task questions</p>
                  <p className="text-[11px] text-neutral-500">
                    Add up to 8 question types. Ground truth is optional — leave blank for subjective questions.
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => setQuestionsPreviewOpen(true)}
                  disabled={(form.questions || []).length === 0}
                  data-testid="admin-preview-questions-btn">
                  <Eye className="mr-1 h-3.5 w-3.5" /> Preview
                </Button>
              </div>
              <QuestionBuilder value={form.questions}
                onChange={(qs) => setForm({ ...form, questions: qs })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Task type</Label>
                <Select value={form.task_type}
                        onValueChange={async (v) => {
                          const tpl = await fetchTemplate(v);
                          const curr = (form.instructions || "").trim();
                          const isKnown = curr && knownTemplatesRef.current.has(form.instructions);
                          const nextInstructions = (!curr || isKnown) && tpl ? tpl : form.instructions;
                          setForm({
                            ...baseForm(v),
                            title: form.title,
                            description: form.description,
                            instructions: nextInstructions,
                            questions: form.questions,
                          });
                        }}>
                  <SelectTrigger data-testid="admin-task-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Language</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Entry cost (pts)</Label>
                <Input type="number" value={form.entry_cost_points}
                       onChange={(e) => setForm({ ...form, entry_cost_points: e.target.value })}
                       data-testid="task-entry-cost" />
              </div>
              <div>
                <Label className="text-xs">Entry cap</Label>
                <Input type="number" value={form.entry_cap}
                       onChange={(e) => setForm({ ...form, entry_cap: e.target.value })}
                       data-testid="task-entry-cap" />
              </div>
              <div>
                <Label className="text-xs"># winners</Label>
                <Input type="number" value={form.winners_count}
                       onChange={(e) => setForm({ ...form, winners_count: e.target.value })}
                       data-testid="task-winners-count" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Task reward (points)</Label>
              <Input type="number" value={form.winner_reward_points}
                     onChange={(e) => setForm({ ...form, winner_reward_points: e.target.value })}
                     data-testid="task-winner-reward" />
              <p className="mt-1 text-xs text-neutral-500" data-testid="task-payout-preview">
                Task reward: <span className="font-semibold text-emerald-700">
                  ₦{(Number(form.winner_reward_points || 0) * 10).toLocaleString()}
                </span>{" · "}
                Total pool: <span className="font-semibold text-emerald-700">
                  ₦{(Number(form.winner_reward_points || 0) * 10 * Number(form.winners_count || 0)).toLocaleString()}
                </span>
              </p>
            </div>

            {/* Audience — country targeting */}
            <div className="rounded-lg border border-neutral-200 p-3 space-y-3" data-testid="task-audience-section">
              <div>
                <Label className="text-xs font-semibold">Audience</Label>
                <p className="mt-0.5 text-[11px] text-neutral-500">
                  Who can see and enter this task.
                </p>
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-xs" data-testid="audience-scope-all">
                  <input
                    type="radio"
                    name="target_scope"
                    checked={form.target_scope === "all"}
                    onChange={() => setForm({ ...form, target_scope: "all", target_countries: [] })}
                    className="accent-[#B45309]"
                  />
                  <span>Everyone (all countries)</span>
                </label>
                <label className="flex items-center gap-2 text-xs" data-testid="audience-scope-specific">
                  <input
                    type="radio"
                    name="target_scope"
                    checked={form.target_scope === "specific"}
                    onChange={() => setForm({ ...form, target_scope: "specific" })}
                    className="accent-[#B45309]"
                  />
                  <span>Specific countries</span>
                </label>
              </div>

              {form.target_scope === "specific" && (
                <div className="space-y-2 border-t border-neutral-100 pt-3">
                  {/* Chip picker */}
                  <div className="flex flex-wrap gap-1.5" data-testid="audience-chip-picker">
                    {(() => {
                      const withUsers = countryFacets.map((f) => f.country);
                      const knownMap = new Map(countries.map((c) => [c.iso2, c.name]));
                      const shown = showAllCountries
                        ? countries.map((c) => c.iso2)
                        : withUsers.length
                          ? withUsers
                          : countries.slice(0, 8).map((c) => c.iso2);
                      return shown.map((iso2) => {
                        const selected = (form.target_countries || []).includes(iso2);
                        const name = knownMap.get(iso2) || iso2;
                        const facet = countryFacets.find((f) => f.country === iso2);
                        return (
                          <button
                            key={iso2}
                            type="button"
                            onClick={() =>
                              setForm({
                                ...form,
                                target_countries: selected
                                  ? form.target_countries.filter((c) => c !== iso2)
                                  : [...(form.target_countries || []), iso2],
                              })
                            }
                            className={
                              selected
                                ? "rounded-full bg-[#B45309] px-2.5 py-1 text-[11px] font-medium text-white ring-1 ring-[#B45309]"
                                : "rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-700 ring-1 ring-neutral-200 hover:ring-[#B45309]/40"
                            }
                            data-testid={`audience-chip-${iso2}`}
                          >
                            {iso2} · {name}
                            {facet ? (
                              <span className={`ml-1 text-[9px] ${selected ? "text-white/80" : "text-neutral-400"}`}>
                                {facet.count}
                              </span>
                            ) : null}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  {!showAllCountries && (
                    <button
                      type="button"
                      onClick={() => setShowAllCountries(true)}
                      className="text-[11px] font-medium text-[#B45309] underline underline-offset-2 hover:text-[#92400E]"
                      data-testid="audience-show-all-btn"
                    >
                      Show all African countries →
                    </button>
                  )}
                  {/* Reach estimate — sum of facet counts for selected chips */}
                  <p className="text-[11px] text-neutral-600" data-testid="audience-reach-preview">
                    Estimated reach:{" "}
                    <span className="font-semibold tabular-nums text-neutral-900">
                      {(() => {
                        const sel = new Set(form.target_countries || []);
                        return countryFacets.filter((f) => sel.has(f.country)).reduce((a, f) => a + f.count, 0);
                      })()}
                    </span>{" "}
                    users in selected countries
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-neutral-200 p-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="perfect-toggle"
                       checked={!!form.require_perfect_score}
                       onChange={(e) => setForm({
                         ...form,
                         require_perfect_score: e.target.checked,
                         // Snap min score to 100 when locking, 70 when unlocking.
                         min_winning_score: e.target.checked ? 100 : (Number(form.min_winning_score) >= 100 ? 70 : Number(form.min_winning_score)),
                       })}
                       data-testid="task-perfect-toggle" />
                <label htmlFor="perfect-toggle" className="flex-1 text-xs">
                  Require perfect score (100/100)
                </label>
              </div>
              {!form.require_perfect_score && (
                <div className="mt-3 border-t border-neutral-100 pt-3">
                  <div className="flex items-center justify-between">
                    <label htmlFor="min-score-slider" className="text-[11px] font-medium text-neutral-700">
                      Minimum score to qualify
                    </label>
                    <span className="text-sm font-semibold tabular-nums text-neutral-900"
                      data-testid="task-min-score-value">
                      {Number(form.min_winning_score) || 70}<span className="text-neutral-400">/100</span>
                    </span>
                  </div>
                  <input id="min-score-slider" type="range" min={50} max={99} step={1}
                    value={Math.min(99, Math.max(50, Number(form.min_winning_score) || 70))}
                    onChange={(e) => setForm({ ...form, min_winning_score: Number(e.target.value) })}
                    className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-neutral-200 accent-[#B45309]"
                    data-testid="task-min-score" />
                  <div className="mt-1 flex justify-between text-[10px] text-neutral-400">
                    <span>50</span><span>99</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Entry deadline</p>
                <DateTimeField
                  isoValue={form.deadline}
                  onChange={(iso) => setForm({ ...form, deadline: iso })}
                  minDate={new Date()}
                  title="Entry deadline"
                  testid="entry-deadline"
                />
              </div>
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Submission deadline</p>
                <DateTimeField
                  isoValue={form.submission_deadline}
                  onChange={(iso) => setForm({ ...form, submission_deadline: iso })}
                  minDate={new Date()}
                  title="Submission deadline"
                  testid="submission-deadline"
                />
              </div>
            </div>

            {/* Dynamic ground-truth section — legacy authoring only.
                Hidden the moment the admin adds questions via the Phase B
                builder (questions[].ground_truth becomes the source). */}
            {(form.questions || []).length === 0 && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                <p className="text-xs font-semibold text-amber-900">
                  Ground truth for {form.task_type.replace(/_/g, " ")}
                </p>
                <p className="mt-0.5 text-[11px] text-amber-800/80">
                  AfriSelection AI scores submissions against these fields.
                </p>
                <div className="mt-3">
                  <PayloadFields task_type={form.task_type}
                                 payload={form.payload}
                                 setPayload={setPayload} />
                </div>
              </div>
            )}

            {/* Collapsible AI grading context (optional but recommended) */}
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/40">
              <button
                type="button"
                onClick={() => setContextOpen((v) => !v)}
                className="flex w-full items-center justify-between p-3 text-left"
                data-testid="ai-context-toggle"
              >
                <div>
                  <p className="text-xs font-semibold text-emerald-800">
                    🤖 Help the AI grade fairly (optional but recommended)
                  </p>
                  <p className="mt-0.5 text-[11px] text-emerald-700/80">
                    Tasks with rich context + calibration examples get ~30% more accurate grading.
                    Take 2 extra minutes for better results.
                  </p>
                </div>
                {contextOpen
                  ? <ChevronUp className="h-4 w-4 text-emerald-700" />
                  : <ChevronDown className="h-4 w-4 text-emerald-700" />}
              </button>
              {contextOpen && (
                <div className="space-y-2 border-t border-emerald-100 p-3">
                  <Field label="Task context — help the AI understand this task">
                    <Textarea rows={3} maxLength={1000}
                              placeholder="Why does this task exist? What's the quality bar? Any nuances the AI should know about? (e.g., 'This voice sample is a rural dialect variation — accept phonetic variations that preserve meaning')"
                              value={form.task_context}
                              onChange={(e) => setForm({ ...form, task_context: e.target.value })}
                              data-testid="ai-task-context" />
                  </Field>
                  <Field label="Grading guidance — how strict should the AI be?">
                    <Textarea rows={2} maxLength={500}
                              placeholder="Specific rules for grading. e.g., 'Accept spelling variations for proper names' or 'Prioritize semantic accuracy over grammatical perfection'"
                              value={form.grading_notes}
                              onChange={(e) => setForm({ ...form, grading_notes: e.target.value })}
                              data-testid="ai-grading-notes" />
                  </Field>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Example of a great submission (100/100)">
                      <Textarea rows={2} maxLength={500}
                                placeholder="Paste an example of what a 100/100 submission looks like."
                                value={form.example_correct}
                                onChange={(e) => setForm({ ...form, example_correct: e.target.value })}
                                data-testid="ai-example-correct" />
                    </Field>
                    <Field label="Example of a poor submission (0-30)">
                      <Textarea rows={2} maxLength={500}
                                placeholder="Paste an example of what a poor submission looks like."
                                value={form.example_incorrect}
                                onChange={(e) => setForm({ ...form, example_incorrect: e.target.value })}
                                data-testid="ai-example-incorrect" />
                    </Field>
                  </div>
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => { setPreviewSample(""); setPreviewResult(null); setPreviewOpen(true); }}
                          data-testid="ai-preview-btn"
                          className="mt-1 border-amber-200 text-amber-800 hover:bg-amber-50">
                    <Sparkles className="mr-1 h-3.5 w-3.5" /> Preview grading
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} className="bg-amber-700 hover:bg-amber-800"
                    data-testid="admin-task-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview grading modal — dry-run the AI grader with current form values */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg" data-testid="preview-grade-dialog">
          <DialogHeader>
            <DialogTitle>Preview AfriSelection AI grading</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              Dry-run the AI grader against a sample submission using this task&apos;s current settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-neutral-600">
              Paste a sample submission (as JSON matching your task type) — the AI will grade it
              using your current context + ground truth + calibration examples.
            </p>
            <Textarea rows={6} placeholder='e.g. {"selected_label":"positive"} or {"tags":["jollof","chicken"]}'
                      value={previewSample}
                      onChange={(e) => setPreviewSample(e.target.value)}
                      data-testid="preview-sample-input" />
            {previewResult && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3 text-sm">
                <p className="text-xs uppercase tracking-wider text-amber-800">Result</p>
                <p className="mt-1 text-lg font-bold text-amber-900" data-testid="preview-score">
                  Score: {previewResult.score ?? "—"}/100
                </p>
                <p className="mt-1 text-xs leading-relaxed text-neutral-700" data-testid="preview-explanation">
                  {previewResult.explanation || "(no explanation returned)"}
                </p>
                {previewResult.error && (
                  <p className="mt-1 text-xs text-red-600">Error: {previewResult.error}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button
              onClick={async () => {
                let parsed = {};
                try { parsed = previewSample.trim() ? JSON.parse(previewSample) : {}; }
                catch { toast.error("Sample must be valid JSON"); return; }
                setPreviewBusy(true);
                try {
                  const r = await adminPreviewGrade({
                    title: form.title || "Preview",
                    task_type: form.task_type,
                    language: form.language,
                    task_payload: form.payload,
                    task_context: form.task_context,
                    grading_notes: form.grading_notes,
                    example_correct: form.example_correct,
                    example_incorrect: form.example_incorrect,
                    sample_submission: parsed,
                  });
                  setPreviewResult(r);
                } catch (e) {
                  toast.error(formatApiError(e, "Preview failed"));
                } finally {
                  setPreviewBusy(false);
                }
              }}
              disabled={previewBusy}
              className="bg-amber-700 hover:bg-amber-800"
              data-testid="preview-run-btn"
            >
              {previewBusy ? "Grading…" : "Grade sample"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase C2: confirm before overwriting Instructions with default template. */}
      <Dialog open={resetTemplateOpen} onOpenChange={setResetTemplateOpen}>
        <DialogContent data-testid="instructions-reset-dialog">
          <DialogHeader>
            <DialogTitle>Replace instructions with the default?</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              This will replace your current instructions with the default template
              for &quot;{form.task_type.replace(/_/g, " ")}&quot;. Custom edits will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetTemplateOpen(false)}
              data-testid="instructions-reset-cancel">Keep mine</Button>
            <Button
              className="bg-[#B45309] hover:bg-amber-800"
              onClick={async () => {
                const tpl = await fetchTemplate(form.task_type);
                if (tpl) {
                  setForm((f) => ({ ...f, instructions: tpl }));
                  toast.success("Instructions reset to default");
                } else {
                  toast.error("No default template for this task type");
                }
                setResetTemplateOpen(false);
              }}
              data-testid="instructions-reset-confirm">
              Use default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase B: Read-only preview of the entire task — how workers will see it. */}
      <Dialog open={questionsPreviewOpen} onOpenChange={setQuestionsPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="questions-preview-dialog">
          <DialogHeader>
            <DialogTitle>Task preview</DialogTitle>
            <DialogDescription className="text-xs text-neutral-500">
              A read-only look at how workers will see this task.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {(form.instructions || "").trim() && (
              <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-amber-900">Instructions</p>
                <p className="whitespace-pre-wrap text-sm text-neutral-800">{form.instructions}</p>
              </div>
            )}
            {(form.questions || []).length === 0 && (
              <p className="rounded-lg bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
                No questions to preview yet.
              </p>
            )}
            {(form.questions || []).map((q, i) => (
              <div key={q.id || i} className="rounded-xl border border-neutral-200 p-4"
                data-testid={`preview-q-${i}`}>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                  Question {i + 1} of {form.questions.length}
                  {q.required === false && <span className="ml-2 text-neutral-400">· optional</span>}
                </p>
                <p className="mb-3 text-base font-medium text-neutral-900">{q.text || <em className="text-neutral-400">Untitled question</em>}</p>
                <PreviewInteractive q={q} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuestionsPreviewOpen(false)}
              data-testid="questions-preview-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function baseForm(task_type) {
  return {
    title: "",
    description: "",
    task_type,
    language: task_type === "translation" ? "Yoruba" : "English",
    entry_cost_points: 10,
    winner_reward_points: 100,
    winners_count: 10,
    entry_cap: 30,
    require_perfect_score: true,
    min_winning_score: 100,
    deadline: "",
    submission_deadline: "",
    payload: emptyPayloadFor(task_type),
    // AfriSelection AI grading context (all optional)
    task_context: "",
    grading_notes: "",
    example_correct: "",
    example_incorrect: "",
    // Phase B: task-question authoring — instructions + questions
    instructions: "",
    questions: [],
    // Country targeting (Feb 2026)
    target_scope: "all",
    target_countries: [],
  };
}
function validateNumericFields(f) {
  const n = (v) => (v === "" || v == null ? NaN : Number(v));
  const cost = n(f.entry_cost_points);
  if (!Number.isFinite(cost) || cost < 0) return "Entry cost must be 0 or greater";
  const reward = n(f.winner_reward_points);
  if (!Number.isFinite(reward) || reward < 1) return "Reward per scorer must be at least 1 point";
  const winners = n(f.winners_count);
  if (!Number.isFinite(winners) || winners < 1) return "Number of scorers must be at least 1";
  const cap = n(f.entry_cap);
  if (!Number.isFinite(cap) || cap < 3) return "Entry cap must be at least 3";
  if (winners > cap) return "Number of scorers cannot exceed the entry cap";
  return null;
}



function validatePayload(task_type, p) {
  if (task_type === "text_classification") {
    if (!p.text?.trim()) return "Ground-truth: 'text' is required";
    if (!Array.isArray(p.labels) || p.labels.length < 2) return "Add at least 2 labels";
    if (!p.correct_label || !p.labels.includes(p.correct_label))
      return "Correct label must be one of the labels";
  } else if (task_type === "image_labeling") {
    if (!p.image_url?.trim()) return "Image URL is required";
    if (!Array.isArray(p.ground_truth_tags) || p.ground_truth_tags.length < 1)
      return "Add at least 1 ground-truth tag";
  } else if (task_type === "translation") {
    if (!p.source_text?.trim()) return "Source text is required";
    if (!p.reference_translation?.trim()) return "Reference translation is required";
  } else if (task_type === "audio_transcription") {
    if (!p.audio_url?.trim()) return "Audio URL is required";
    if (!p.reference_transcript?.trim()) return "Reference transcript is required";
  } else if (task_type === "prompt_rating" || task_type === "voice_rating") {
    if (!p.prompt?.trim()) return "Prompt is required";
    if (!p.response?.trim()) return "Response is required";
    if (!Array.isArray(p.criteria) || p.criteria.length < 1) return "Add at least 1 criterion";
  }
  return null;
}

// ------ Dynamic per-task-type ground-truth fields --------------------------
function PayloadFields({ task_type, payload, setPayload }) {
  if (task_type === "text_classification") return <TextClassifFields p={payload} set={setPayload} />;
  if (task_type === "image_labeling") return <ImageLabelFields p={payload} set={setPayload} />;
  if (task_type === "translation") return <TranslationFields p={payload} set={setPayload} />;
  if (task_type === "audio_transcription") return <AudioFields p={payload} set={setPayload} />;
  if (task_type === "prompt_rating" || task_type === "voice_rating")
    return <RatingFields p={payload} set={setPayload} />;
  return null;
}

function TextClassifFields({ p, set }) {
  return (
    <div className="space-y-2">
      <Field label="Text to classify">
        <Textarea rows={2} value={p.text || ""}
                  onChange={(e) => set({ text: e.target.value })}
                  data-testid="gt-text" />
      </Field>
      <Field label="Labels (comma-separated)">
        <Input value={(p.labels || []).join(", ")}
               onChange={(e) => set({ labels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
               data-testid="gt-labels" />
      </Field>
      <Field label="Correct label (ground truth)">
        <Select value={p.correct_label || ""} onValueChange={(v) => set({ correct_label: v })}>
          <SelectTrigger data-testid="gt-correct-label"><SelectValue placeholder="Pick correct label" /></SelectTrigger>
          <SelectContent>
            {(p.labels || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function ImageLabelFields({ p, set }) {
  return (
    <div className="space-y-2">
      <Field label="Image URL"><Input value={p.image_url || ""} onChange={(e) => set({ image_url: e.target.value })} data-testid="gt-image-url" /></Field>
      <Field label="Ground-truth tags">
        <TagsInput tags={p.ground_truth_tags || []} setTags={(v) => set({ ground_truth_tags: v })} testid="gt-tags" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Min tags"><Input type="number" value={p.min_tags ?? 3} onChange={(e) => set({ min_tags: Number(e.target.value) })} /></Field>
        <Field label="Max tags"><Input type="number" value={p.max_tags ?? 10} onChange={(e) => set({ max_tags: Number(e.target.value) })} /></Field>
      </div>
    </div>
  );
}

function TranslationFields({ p, set }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Source language">
          <Select value={p.source_language || "English"} onValueChange={(v) => set({ source_language: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Target language">
          <Select value={p.target_language || "Yoruba"} onValueChange={(v) => set({ target_language: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Source text"><Textarea rows={2} value={p.source_text || ""} onChange={(e) => set({ source_text: e.target.value })} data-testid="gt-source-text" /></Field>
      <Field label="Reference translation (ground truth)">
        <Textarea rows={2} value={p.reference_translation || ""} onChange={(e) => set({ reference_translation: e.target.value })} data-testid="gt-ref-translation" />
      </Field>
      <Field label="Min characters"><Input type="number" value={p.min_chars ?? 20} onChange={(e) => set({ min_chars: Number(e.target.value) })} /></Field>
    </div>
  );
}

function AudioFields({ p, set }) {
  return (
    <div className="space-y-2">
      <Field label="Audio URL"><Input value={p.audio_url || ""} onChange={(e) => set({ audio_url: e.target.value })} data-testid="gt-audio-url" /></Field>
      <Field label="Language">
        <Select value={p.language || "English"} onValueChange={(v) => set({ language: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Reference transcript (ground truth)">
        <Textarea rows={3} value={p.reference_transcript || ""} onChange={(e) => set({ reference_transcript: e.target.value })} data-testid="gt-ref-transcript" />
      </Field>
    </div>
  );
}

function RatingFields({ p, set }) {
  const criteria = p.criteria || [];
  const setCriterion = (idx, v) => {
    const next = [...criteria];
    const oldName = next[idx];
    next[idx] = v;
    const ratings = { ...(p.expected_ratings || {}) };
    if (oldName && ratings[oldName] != null) {
      ratings[v] = ratings[oldName];
      delete ratings[oldName];
    }
    set({ criteria: next, expected_ratings: ratings });
  };
  const removeCriterion = (idx) => {
    const name = criteria[idx];
    const next = criteria.filter((_, i) => i !== idx);
    const ratings = { ...(p.expected_ratings || {}) };
    delete ratings[name];
    set({ criteria: next, expected_ratings: ratings });
  };
  const addCriterion = () => {
    const name = `Criterion ${criteria.length + 1}`;
    set({ criteria: [...criteria, name], expected_ratings: { ...(p.expected_ratings || {}), [name]: 5 } });
  };
  return (
    <div className="space-y-2">
      <Field label="Prompt / clip description">
        <Textarea rows={2} value={p.prompt || ""} onChange={(e) => set({ prompt: e.target.value })} data-testid="gt-prompt" />
      </Field>
      <Field label="Response / clip to rate">
        <Textarea rows={2} value={p.response || ""} onChange={(e) => set({ response: e.target.value })} data-testid="gt-response" />
      </Field>
      <Field label="Rating criteria + ground-truth scores (1-5)">
        <div className="space-y-1.5" data-testid="gt-criteria">
          {criteria.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input value={c} onChange={(e) => setCriterion(idx, e.target.value)} className="flex-1" />
              <Input type="number" min={1} max={5}
                     value={p.expected_ratings?.[c] ?? 5}
                     onChange={(e) => set({ expected_ratings: { ...(p.expected_ratings || {}), [c]: Number(e.target.value) } })}
                     className="w-16" />
              <Button variant="ghost" size="sm" onClick={() => removeCriterion(idx)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addCriterion} data-testid="gt-add-criterion">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add criterion
          </Button>
        </div>
      </Field>
      <Field label="Expected overall (1-5)">
        <Input type="number" min={1} max={5} value={p.expected_overall ?? 5}
               onChange={(e) => set({ expected_overall: Number(e.target.value) })} />
      </Field>
    </div>
  );
}

function TagsInput({ tags, setTags, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t || tags.includes(t)) { setDraft(""); return; }
    setTags([...tags, t]);
    setDraft("");
  };
  return (
    <div data-testid={testid}>
      <Input value={draft}
             onChange={(e) => setDraft(e.target.value)}
             onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
             placeholder="Type a tag, press Enter" />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {tags.map((t, i) => (
          <Badge key={i} className="cursor-pointer bg-amber-100 text-amber-900 hover:bg-amber-200"
                 onClick={() => setTags(tags.filter((x) => x !== t))}>
            {t} <X className="ml-1 h-3 w-3" />
          </Badge>
        ))}
        {tags.length === 0 && <p className="text-[11px] text-neutral-400">No tags yet</p>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-neutral-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Wraps `QuestionPreview` with local state so admins can interact with the
 * preview modal (kick tyres) — value never leaves this component. */
function PreviewInteractive({ q }) {
  const [v, setV] = useState(undefined);
  return <QuestionPreview question={q} value={v} onChange={setV} />;
}


// --------------------------------------------------------------------------
// DateTimeField: DatePickerSheet button + separate 24-hour time dropdown.
// Stores value as ISO 8601 string. Time granularity: 30-min steps.
// TODO(follow-up): promote this into a shared component + build a proper
// datetime picker that combines date + time into a single sheet.
// --------------------------------------------------------------------------
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      opts.push(`${hh}:${mm}`);
    }
  }
  return opts;
})();

function DateTimeField({ isoValue, onChange, minDate, title, testid }) {
  const [open, setOpen] = useState(false);
  const parsed = isoValue ? new Date(isoValue) : null;
  const validDate = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
  const timeStr = validDate
    ? `${String(validDate.getHours()).padStart(2, "0")}:${String(validDate.getMinutes()).padStart(2, "0")}`
    : "09:00";

  const emitCombined = (datePart, hhmm) => {
    if (!datePart) return;
    const [hh, mm] = hhmm.split(":").map((n) => parseInt(n, 10));
    const d = new Date(datePart);
    d.setHours(hh, mm, 0, 0);
    onChange?.(d.toISOString());
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testid}-date-btn`}
        className="inline-flex flex-1 items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-2 text-left text-sm text-neutral-900 hover:border-neutral-300"
      >
        <CalendarIcon className="h-3.5 w-3.5 text-neutral-500" />
        {validDate ? (
          <span>{fmtDate(validDate, "EEE, MMM d yyyy")}</span>
        ) : (
          <span className="text-neutral-400">Select date</span>
        )}
      </button>
      <Select
        value={timeStr}
        onValueChange={(v) => emitCombined(validDate || new Date(), v)}
      >
        <SelectTrigger
          className="w-[92px] shrink-0"
          data-testid={`${testid}-time-select`}
        >
          <SelectValue placeholder="Time" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {TIME_OPTIONS.map((t) => (
            <SelectItem key={t} value={t} className="font-mono">{t}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DatePickerSheet
        open={open}
        onOpenChange={setOpen}
        value={validDate}
        minDate={minDate}
        title={title}
        onChange={(d) => emitCombined(d, timeStr)}
      />
    </div>
  );
}
